import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navbar from "@/components/karmivo/Navbar";
import { api } from "@/lib/api";
import { collectPayment } from "@/lib/razorpay";
import { CalendarDays, CheckCircle2, CreditCard, Loader2, MapPin, AlertTriangle } from "lucide-react";

const errText = (e) => e.response?.data?.detail || e.message || "Something went wrong";

export default function PayRetry() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/pay/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(errText(e)));
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data: order } = await api.post(`/pay/${token}/order`);
      const rzp = await collectPayment(order, { description: `Booking ${info.booking_code}`, prefill: { name: info.customer_name } });
      await api.post(`/pay/${token}/verify`, rzp);
      setDone(true);
      toast.success("Payment received. Your booking is confirmed.");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const paid = done || info?.paid;
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold">Complete your payment</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">{error ? "Payment link unavailable" : "Your booking is saved"}</h1>
        {error && (
          <Card className="p-5 mt-6 border-red-200 bg-red-50 text-red-700 text-sm flex gap-3" data-testid="pay-retry-error">
            <AlertTriangle size={18} className="shrink-0"/><div>{error} <Link to="/track" className="underline">Track a booking</Link></div>
          </Card>
        )}
        {!info && !error && <Loader2 className="animate-spin text-orange-500 mt-8" size={28} data-testid="pay-retry-loading"/>}
        {info && (
          <Card className="p-6 mt-6" data-testid="pay-retry-card">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-sm text-slate-500" data-testid="pay-retry-code">{info.booking_code}</span>
              <span className="text-xs uppercase tracking-wider text-slate-400">{info.stage === "full" ? "Full payment" : `${info.stage} payment`}</span>
            </div>
            <div className="font-display text-4xl font-bold text-slate-900 mt-3" data-testid="pay-retry-amount">₹{Number(info.amount).toLocaleString("en-IN")}</div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-600">
              {info.category && <div className="capitalize">{info.category.replace(/_/g, " ")}</div>}
              {info.scheduled_date && <div className="flex items-center gap-2"><CalendarDays size={14}/>{info.scheduled_date} {info.start_time || ""}</div>}
              {info.location && <div className="flex items-center gap-2"><MapPin size={14}/>{info.location}</div>}
            </div>
            {paid ? (
              <div className="mt-6 flex items-center gap-2 text-emerald-600 font-semibold" data-testid="pay-retry-paid"><CheckCircle2 size={18}/>Payment complete. We're finding your workers.</div>
            ) : info.closed ? (
              <div className="mt-6 text-sm text-slate-500" data-testid="pay-retry-closed">This booking is closed and can no longer be paid.</div>
            ) : (
              <>
                <Button className="w-full mt-6 h-12 bg-orange-500 hover:bg-orange-600 text-base" onClick={pay} disabled={busy} data-testid="pay-retry-pay-btn">
                  {busy ? <Loader2 size={18} className="animate-spin mr-2"/> : <CreditCard size={18} className="mr-2"/>}Pay ₹{Number(info.amount).toLocaleString("en-IN")} now
                </Button>
                <p className="text-xs text-slate-400 mt-3 text-center">Secure payment by Razorpay · Link valid until {new Date(info.expires_at).toLocaleString()}</p>
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
